import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { COMPLETE_AWARD, GET_MY_AWARDS } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useCurrentUser } from "./useCurrentUser";
import { useReactQueryErrorReporter } from "./use-error-reporter";

export interface EarnedAward {
  id: string;
  awardID: string;
  completedAt?: string | null;
  path?: string | null;
  shardID?: string | null;
}

interface GetMyAwardsResponse {
  getAwards: EarnedAward[];
}

export interface CompleteAwardInput {
  awardId: string | number;
  awardType: string;
  wasAssigned?: boolean;
}

export interface CompleteAwardResult {
  id: string;
  awardID: string;
  climberID?: string;
  parentID?: string;
  completedAt?: string;
  path?: string;
  shardID?: string;
}

interface CompleteAwardResponse {
  completeAward: CompleteAwardResult;
}

export const useGetMyAwards = () => {
  const { error, data, ...rest } = useQuery<EarnedAward[]>({
    queryKey: ["awards"],
    queryFn: async () => {
      const resp =
        await graphQLClient.request<GetMyAwardsResponse>(GET_MY_AWARDS);
      return resp.getAwards;
    },
  });

  useReactQueryErrorReporter("Error fetching awards", error, false);

  return { ...rest, error, data: data || [] };
};

export const useCompleteAward = () => {
  const currentUser = useCurrentUser() as {
    shardID?: string;
    userPath?: string;
  };
  const queryClient = useQueryClient();

  const mutation = useMutation<CompleteAwardResult, Error, CompleteAwardInput>({
    mutationFn: async ({ awardId, awardType }) => {
      const { completeAward } =
        await graphQLClient.request<CompleteAwardResponse>(COMPLETE_AWARD, {
          input: {
            awardId,
            awardType,
            shardID: currentUser.shardID,
            userPath: currentUser.userPath,
          },
        });
      return completeAward;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["awards"] });
    },
  });

  return { completeAward: mutation };
};
